import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Check, X, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/dates";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Vehicle = {
  vehicle_number: string;
  make: string | null;
  model: string | null;
  year: number | null;
  vehicle_type: string | null;
};

type SetupFormData = {
  usage_change: string;
  initial_cof: string;
  fuel_type: string;
  fleet_details: string;
  airport_barrier: string;
  seats: number;
  seats_added_taxicaller: boolean;
  tmv_wheelchair: string;
  tmv_added_taxicaller: boolean;
  non_van: string;
  non_van_added_taxicaller: boolean;
  car_seat: string;
  car_seat_added_taxicaller: boolean;
  screen: string;
  screen_added_taxicaller: boolean;
  tgb_vehicle: string;
  tgb_vehicle_added_taxicaller: boolean;
  psl_holder_required: string;
  psl_holder_progress: string;
  loading_certificate_required: string;
  loading_certificate_progress: string;
  kiddilock_signs_required: string;
  kiddilock_signs_progress: string;
  fleet_number_required: string;
  fleet_number_progress: string;
  door_sign_required: string;
  door_sign_progress: string;
  fleet_numbers_lr_required: string;
  fleet_numbers_lr_progress: string;
  fleet_number_rear_required: string;
  fleet_number_rear_progress: string;
  taxi_stand_coupon_required: string;
  taxi_stand_coupon_progress: string;
  roof_rack_required: string;
  roof_rack_progress: string;
  top_light_required: string;
  top_light_progress: string;
  camera_required: string;
  camera_progress: string;
  hardwire_usb_required: string;
  hardwire_usb_progress: string;
  eftpos_machine_required: string;
  eftpos_machine_progress: string;
  tablet_holder_required: string;
  tablet_holder_progress: string;
  eftpos_charger_required: string;
  eftpos_charger_progress: string;
  driver_seat_inspection1: string;
  driver_seat_inspection2: string;
  front_passenger_inspection1: string;
  front_passenger_inspection2: string;
  rear_seats_inspection1: string;
  rear_seats_inspection2: string;
  interior_inspection1: string;
  interior_inspection2: string;
  notes: string;
  completed_by: string;
  completed_date: Date | null;
};

const defaultFormData: SetupFormData = {
  usage_change: "not_completed",
  initial_cof: "not_completed",
  fuel_type: "petrol",
  fleet_details: "n/a",
  airport_barrier: "not_completed",
  seats: 4,
  seats_added_taxicaller: false,
  tmv_wheelchair: "no",
  tmv_added_taxicaller: false,
  non_van: "no",
  non_van_added_taxicaller: false,
  car_seat: "no",
  car_seat_added_taxicaller: false,
  screen: "no",
  screen_added_taxicaller: false,
  tgb_vehicle: "no",
  tgb_vehicle_added_taxicaller: false,
  psl_holder_required: "required",
  psl_holder_progress: "not_completed",
  loading_certificate_required: "required",
  loading_certificate_progress: "not_completed",
  kiddilock_signs_required: "required",
  kiddilock_signs_progress: "not_completed",
  fleet_number_required: "required",
  fleet_number_progress: "not_completed",
  door_sign_required: "required",
  door_sign_progress: "not_completed",
  fleet_numbers_lr_required: "required",
  fleet_numbers_lr_progress: "not_completed",
  fleet_number_rear_required: "required",
  fleet_number_rear_progress: "not_completed",
  taxi_stand_coupon_required: "required",
  taxi_stand_coupon_progress: "not_completed",
  roof_rack_required: "required",
  roof_rack_progress: "not_completed",
  top_light_required: "required",
  top_light_progress: "not_completed",
  camera_required: "required",
  camera_progress: "not_completed",
  hardwire_usb_required: "required",
  hardwire_usb_progress: "not_completed",
  eftpos_machine_required: "required",
  eftpos_machine_progress: "not_completed",
  tablet_holder_required: "required",
  tablet_holder_progress: "not_completed",
  eftpos_charger_required: "required",
  eftpos_charger_progress: "not_completed",
  driver_seat_inspection1: "pass",
  driver_seat_inspection2: "",
  front_passenger_inspection1: "pass",
  front_passenger_inspection2: "",
  rear_seats_inspection1: "pass",
  rear_seats_inspection2: "",
  interior_inspection1: "pass",
  interior_inspection2: "",
  notes: "",
  completed_by: "",
  completed_date: null,
};

interface VehicleSetupEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle | null;
  onSaved: () => void;
}

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-gray-700/60 bg-gray-900/30 p-4">
    <div className="mb-3">
      <div className="text-sm font-semibold text-amber-500">{title}</div>
      {subtitle ? <div className="text-xs text-gray-400 mt-1">{subtitle}</div> : null}
    </div>
    {children}
  </div>
);

const VehicleSetupEditModal: React.FC<VehicleSetupEditModalProps> = ({ isOpen, onClose, vehicle, onSaved }) => {
  const [formData, setFormData] = useState<SetupFormData>(defaultFormData);
  const [saving, setSaving] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState<string | null>(null);
  const [defaultFuelType, setDefaultFuelType] = useState("petrol");

  useEffect(() => {
    // Prefill new setup records' fuel type from Settings > Fleet Defaults
    // (fleet_default_fuel_type) -- previously configured there but never
    // actually read anywhere.
    supabase
      .from("settings")
      .select("value")
      .eq("name", "fleet_default_fuel_type")
      .maybeSingle()
      .then(({ data }) => {
        if (!data?.value) return;
        // Fleet Defaults offers Petrol/Diesel/Hybrid/Electric/Other, but this
        // form only supports petrol/petrol_hybrid/diesel -- map what we can,
        // fall back to petrol for options with no counterpart here yet.
        const normalized = String(data.value).toLowerCase();
        if (normalized === "diesel") setDefaultFuelType("diesel");
        else if (normalized === "hybrid") setDefaultFuelType("petrol_hybrid");
        else if (normalized === "petrol") setDefaultFuelType("petrol");
      });
  }, []);

  const loadExistingSetup = useCallback(async (vNum: string) => {
    const { data, error } = await supabase
      .from("vehicle_setup_records")
      .select("*")
      .eq("vehicle_number", vNum)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("Error loading existing setup:", error);
      return;
    }

    if (data) {
      setExistingRecordId(data.id);
      setFormData({
        usage_change: data.usage_change || "not_completed",
        initial_cof: data.initial_cof || "not_completed",
        fuel_type: data.fuel_type || "petrol",
        fleet_details: data.fleet_details || "n/a",
        airport_barrier: data.airport_barrier || "not_completed",
        seats: data.seats || 4,
        seats_added_taxicaller: data.seats_added_taxicaller || false,
        tmv_wheelchair: data.tmv_wheelchair || "no",
        tmv_added_taxicaller: data.tmv_added_taxicaller || false,
        non_van: data.non_van || "no",
        non_van_added_taxicaller: data.non_van_added_taxicaller || false,
        car_seat: data.car_seat || "no",
        car_seat_added_taxicaller: data.car_seat_added_taxicaller || false,
        screen: data.screen || "no",
        screen_added_taxicaller: data.screen_added_taxicaller || false,
        tgb_vehicle: data.tgb_vehicle || "no",
        tgb_vehicle_added_taxicaller: data.tgb_vehicle_added_taxicaller || false,
        psl_holder_required: data.psl_holder_required || "required",
        psl_holder_progress: data.psl_holder_progress || "not_completed",
        loading_certificate_required: data.loading_certificate_required || "required",
        loading_certificate_progress: data.loading_certificate_progress || "not_completed",
        kiddilock_signs_required: data.kiddilock_signs_required || "required",
        kiddilock_signs_progress: data.kiddilock_signs_progress || "not_completed",
        fleet_number_required: data.fleet_number_required || "required",
        fleet_number_progress: data.fleet_number_progress || "not_completed",
        door_sign_required: data.door_sign_required || "required",
        door_sign_progress: data.door_sign_progress || "not_completed",
        fleet_numbers_lr_required: data.fleet_numbers_lr_required || "required",
        fleet_numbers_lr_progress: data.fleet_numbers_lr_progress || "not_completed",
        fleet_number_rear_required: data.fleet_number_rear_required || "required",
        fleet_number_rear_progress: data.fleet_number_rear_progress || "not_completed",
        taxi_stand_coupon_required: data.taxi_stand_coupon_required || "required",
        taxi_stand_coupon_progress: data.taxi_stand_coupon_progress || "not_completed",
        roof_rack_required: data.roof_rack_required || "required",
        roof_rack_progress: data.roof_rack_progress || "not_completed",
        top_light_required: data.top_light_required || "required",
        top_light_progress: data.top_light_progress || "not_completed",
        camera_required: data.camera_required || "required",
        camera_progress: data.camera_progress || "not_completed",
        hardwire_usb_required: data.hardwire_usb_required || "required",
        hardwire_usb_progress: data.hardwire_usb_progress || "not_completed",
        eftpos_machine_required: data.eftpos_machine_required || "required",
        eftpos_machine_progress: data.eftpos_machine_progress || "not_completed",
        tablet_holder_required: data.tablet_holder_required || "required",
        tablet_holder_progress: data.tablet_holder_progress || "not_completed",
        eftpos_charger_required: data.eftpos_charger_required || "required",
        eftpos_charger_progress: data.eftpos_charger_progress || "not_completed",
        driver_seat_inspection1: data.driver_seat_inspection1 || "pass",
        driver_seat_inspection2: data.driver_seat_inspection2 || "",
        front_passenger_inspection1: data.front_passenger_inspection1 || "pass",
        front_passenger_inspection2: data.front_passenger_inspection2 || "",
        rear_seats_inspection1: data.rear_seats_inspection1 || "pass",
        rear_seats_inspection2: data.rear_seats_inspection2 || "",
        interior_inspection1: data.interior_inspection1 || "pass",
        interior_inspection2: data.interior_inspection2 || "",
        notes: data.notes || "",
        completed_by: data.completed_by || "",
        completed_date: data.completed_date ? new Date(data.completed_date) : null,
      });
    } else {
      setExistingRecordId(null);
      setFormData({ ...defaultFormData, fuel_type: defaultFuelType });
    }
  }, [defaultFuelType]);

  useEffect(() => {
    if (vehicle && isOpen) {
      loadExistingSetup(vehicle.vehicle_number);
    }
  }, [vehicle, isOpen, loadExistingSetup]);

  const updateField = <K extends keyof SetupFormData>(field: K, value: SetupFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isVehicleProfilesComplete = useMemo(() => {
    return (
      formData.seats_added_taxicaller &&
      formData.tmv_added_taxicaller &&
      formData.non_van_added_taxicaller &&
      formData.car_seat_added_taxicaller &&
      formData.screen_added_taxicaller &&
      formData.tgb_vehicle_added_taxicaller
    );
  }, [formData]);

  const isSignageComplete = useMemo(() => {
    const items = [
      { req: formData.psl_holder_required, prog: formData.psl_holder_progress },
      { req: formData.loading_certificate_required, prog: formData.loading_certificate_progress },
      { req: formData.kiddilock_signs_required, prog: formData.kiddilock_signs_progress },
      { req: formData.fleet_number_required, prog: formData.fleet_number_progress },
      { req: formData.door_sign_required, prog: formData.door_sign_progress },
      { req: formData.fleet_numbers_lr_required, prog: formData.fleet_numbers_lr_progress },
      { req: formData.fleet_number_rear_required, prog: formData.fleet_number_rear_progress },
      { req: formData.taxi_stand_coupon_required, prog: formData.taxi_stand_coupon_progress },
    ];
    return items.every((item) => item.req === "n/a" || item.prog === "completed");
  }, [formData]);

  const isHardwareComplete = useMemo(() => {
    const items = [
      { req: formData.roof_rack_required, prog: formData.roof_rack_progress },
      { req: formData.top_light_required, prog: formData.top_light_progress },
      { req: formData.camera_required, prog: formData.camera_progress },
      { req: formData.hardwire_usb_required, prog: formData.hardwire_usb_progress },
      { req: formData.eftpos_machine_required, prog: formData.eftpos_machine_progress },
      { req: formData.tablet_holder_required, prog: formData.tablet_holder_progress },
      { req: formData.eftpos_charger_required, prog: formData.eftpos_charger_progress },
    ];
    return items.every((item) => item.req === "n/a" || item.prog === "completed");
  }, [formData]);

  const isInspectionComplete = useMemo(() => {
    const inspections = [
      { i1: formData.driver_seat_inspection1, i2: formData.driver_seat_inspection2 },
      { i1: formData.front_passenger_inspection1, i2: formData.front_passenger_inspection2 },
      { i1: formData.rear_seats_inspection1, i2: formData.rear_seats_inspection2 },
      { i1: formData.interior_inspection1, i2: formData.interior_inspection2 },
    ];
    return inspections.every((insp) => {
      if (insp.i1 === "pass") return true;
      if (insp.i1 === "rectify" && insp.i2 === "pass") return true;
      return false;
    });
  }, [formData]);

  const saveSetup = async () => {
    if (!vehicle) return;

    setSaving(true);

    try {
      const payload = {
        vehicle_number: vehicle.vehicle_number,
        ...formData,
        completed_date: formData.completed_date ? format(formData.completed_date, "yyyy-MM-dd") : null,
      };

      if (existingRecordId) {
        const { error } = await supabase
          .from("vehicle_setup_records")
          .update(payload)
          .eq("id", existingRecordId);

        if (error) throw error;
      } else {
        // Capture the new row's id so a second Save (the modal no longer
        // closes after saving) updates this same record instead of
        // inserting a duplicate.
        const { data, error } = await supabase
          .from("vehicle_setup_records")
          .insert(payload)
          .select("id")
          .single();

        if (error) throw error;
        setExistingRecordId(data.id);
      }

      toast({
        title: "Setup saved",
        description: "Vehicle setup has been recorded successfully.",
      });

      onSaved();
    } catch (e: any) {
      toast({
        title: "Error saving setup",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(defaultFormData);
    setExistingRecordId(null);
    onClose();
  };

  if (!vehicle) return null;

  const CompletionDropdown = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[160px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="completed">Completed</SelectItem>
        <SelectItem value="not_completed">Not Completed</SelectItem>
      </SelectContent>
    </Select>
  );

  const YesNoDropdown = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[90px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="yes">Yes</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
  );

  const RequiredNADropdown = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[120px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="required">Required</SelectItem>
        <SelectItem value="n/a">N/A</SelectItem>
      </SelectContent>
    </Select>
  );

  const ProgressDropdown = ({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) => (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn("w-[160px]", disabled && "opacity-50")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="completed">Completed</SelectItem>
        <SelectItem value="not_completed">Not Completed</SelectItem>
      </SelectContent>
    </Select>
  );

  const InspectionDropdown = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[110px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="pass">Pass</SelectItem>
        <SelectItem value="rectify">Rectify</SelectItem>
      </SelectContent>
    </Select>
  );


  const StatusBadge = ({ complete }: { complete: boolean }) => (
    <Badge variant={complete ? "default" : "secondary"} className={cn(complete ? "bg-green-600" : "bg-yellow-600", "text-xs")}>
      {complete ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
      {complete ? "Complete" : "Incomplete"}
    </Badge>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <DialogHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10 pb-4 border-b border-gray-700">
          <DialogTitle className="text-lg font-semibold text-amber-500">
            Vehicle Setup: {vehicle.vehicle_number} - {vehicle.make} {vehicle.model} ({vehicle.year})
          </DialogTitle>
          <div className="flex gap-2">
            <Button onClick={saveSetup} disabled={saving} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : existingRecordId ? "Update Setup" : "Save Setup"}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pt-4">
          {/* Vehicle Details */}
          <Section title="Vehicle Details" subtitle="Core vehicle setup information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Usage Change</Label>
                <CompletionDropdown value={formData.usage_change} onChange={(v) => updateField("usage_change", v)} />
              </div>
              <div className="space-y-2">
                <Label>Initial COF</Label>
                <CompletionDropdown value={formData.initial_cof} onChange={(v) => updateField("initial_cof", v)} />
              </div>
              <div className="space-y-2">
                <Label>Fuel Type</Label>
                <Select value={formData.fuel_type} onValueChange={(v) => updateField("fuel_type", v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="petrol">Petrol</SelectItem>
                    <SelectItem value="petrol_hybrid">Petrol Hybrid</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fleet Details</Label>
                <Select value={formData.fleet_details} onValueChange={(v) => updateField("fleet_details", v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stss">STSS</SelectItem>
                    <SelectItem value="tgb">TG&B</SelectItem>
                    <SelectItem value="n/a">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Airport Barrier</Label>
                <Select value={formData.airport_barrier} onValueChange={(v) => updateField("airport_barrier", v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="not_completed">Not Completed</SelectItem>
                    <SelectItem value="n/a">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          {/* Vehicle Profiles */}
          <Section title="Vehicle Profiles" subtitle="TaxiCaller profile settings">
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-2 rounded bg-muted/20">
                <div className="w-28 text-sm">SEATS</div>
                <Select value={formData.seats.toString()} onValueChange={(v) => updateField("seats", parseInt(v))}>
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Checkbox id="seats_tc" checked={formData.seats_added_taxicaller} onCheckedChange={(c) => updateField("seats_added_taxicaller", !!c)} />
                  <Label htmlFor="seats_tc" className="text-xs cursor-pointer">TaxiCaller</Label>
                </div>
              </div>
              {[
                { label: "TMV (Wheelchair)", key: "tmv_wheelchair", tcKey: "tmv_added_taxicaller" },
                { label: "NON VAN", key: "non_van", tcKey: "non_van_added_taxicaller" },
                { label: "CAR SEAT", key: "car_seat", tcKey: "car_seat_added_taxicaller" },
                { label: "SCREEN", key: "screen", tcKey: "screen_added_taxicaller" },
                { label: "TGB VEHICLE", key: "tgb_vehicle", tcKey: "tgb_vehicle_added_taxicaller" },
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-3 p-2 rounded bg-muted/20">
                  <div className="w-28 text-sm">{item.label}</div>
                  <YesNoDropdown value={formData[item.key as keyof SetupFormData] as string} onChange={(v) => updateField(item.key as keyof SetupFormData, v)} />
                  <div className="flex items-center gap-2">
                    <Checkbox id={item.tcKey} checked={formData[item.tcKey as keyof SetupFormData] as boolean} onCheckedChange={(c) => updateField(item.tcKey as keyof SetupFormData, !!c)} />
                    <Label htmlFor={item.tcKey} className="text-xs cursor-pointer">TaxiCaller</Label>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Signage */}
          <Section title="Signage" subtitle="Required signage and completion status">
            <div className="space-y-2">
              {[
                { label: "PSL Holder", reqKey: "psl_holder_required", progKey: "psl_holder_progress" },
                { label: "Loading Certificate", reqKey: "loading_certificate_required", progKey: "loading_certificate_progress" },
                { label: "Kiddilock Signs", reqKey: "kiddilock_signs_required", progKey: "kiddilock_signs_progress" },
                { label: "Fleet Number", reqKey: "fleet_number_required", progKey: "fleet_number_progress" },
                { label: "Door Sign", reqKey: "door_sign_required", progKey: "door_sign_progress" },
                { label: "Fleet Numbers L/R", reqKey: "fleet_numbers_lr_required", progKey: "fleet_numbers_lr_progress" },
                { label: "Fleet Number Rear", reqKey: "fleet_number_rear_required", progKey: "fleet_number_rear_progress" },
                { label: "Taxi Stand Coupon", reqKey: "taxi_stand_coupon_required", progKey: "taxi_stand_coupon_progress" },
              ].map((item) => (
                <div key={item.reqKey} className="flex items-center gap-3 p-2 rounded bg-muted/20">
                  <div className="w-32 text-sm">{item.label}</div>
                  <RequiredNADropdown value={formData[item.reqKey as keyof SetupFormData] as string} onChange={(v) => updateField(item.reqKey as keyof SetupFormData, v)} />
                  <ProgressDropdown value={formData[item.progKey as keyof SetupFormData] as string} onChange={(v) => updateField(item.progKey as keyof SetupFormData, v)} disabled={formData[item.reqKey as keyof SetupFormData] === "n/a"} />
                </div>
              ))}
            </div>
          </Section>

          {/* Hardware */}
          <Section title="Hardware" subtitle="Required hardware and completion status">
            <div className="space-y-2">
              {[
                { label: "Roof Rack", reqKey: "roof_rack_required", progKey: "roof_rack_progress" },
                { label: "Top Light", reqKey: "top_light_required", progKey: "top_light_progress" },
                { label: "Camera", reqKey: "camera_required", progKey: "camera_progress" },
                { label: "Hardwire USB", reqKey: "hardwire_usb_required", progKey: "hardwire_usb_progress" },
                { label: "Eftpos Machine", reqKey: "eftpos_machine_required", progKey: "eftpos_machine_progress" },
                { label: "Tablet Holder", reqKey: "tablet_holder_required", progKey: "tablet_holder_progress" },
                { label: "Eftpos Charger", reqKey: "eftpos_charger_required", progKey: "eftpos_charger_progress" },
              ].map((item) => (
                <div key={item.reqKey} className="flex items-center gap-3 p-2 rounded bg-muted/20">
                  <div className="w-32 text-sm">{item.label}</div>
                  <RequiredNADropdown value={formData[item.reqKey as keyof SetupFormData] as string} onChange={(v) => updateField(item.reqKey as keyof SetupFormData, v)} />
                  <ProgressDropdown value={formData[item.progKey as keyof SetupFormData] as string} onChange={(v) => updateField(item.progKey as keyof SetupFormData, v)} disabled={formData[item.reqKey as keyof SetupFormData] === "n/a"} />
                </div>
              ))}
            </div>
          </Section>

          {/* Inspection - Full Width */}
          <div className="xl:col-span-2">
            <Section title="Inspection" subtitle="Vehicle inspection results">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: "Driver Seat", i1Key: "driver_seat_inspection1", i2Key: "driver_seat_inspection2" },
                  { label: "Front Passenger", i1Key: "front_passenger_inspection1", i2Key: "front_passenger_inspection2" },
                  { label: "Rear Seats", i1Key: "rear_seats_inspection1", i2Key: "rear_seats_inspection2" },
                  { label: "Interior", i1Key: "interior_inspection1", i2Key: "interior_inspection2" },
                ].map((item) => {
                  const i1Value = formData[item.i1Key as keyof SetupFormData] as string;
                  const showI2 = i1Value === "rectify";
                  return (
                    <div key={item.i1Key} className="flex flex-col gap-2 p-3 rounded bg-muted/20">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-12">Insp 1:</span>
                        <InspectionDropdown value={i1Value} onChange={(v) => updateField(item.i1Key as keyof SetupFormData, v)} />
                      </div>
                      {showI2 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-12">Insp 2:</span>
                          <InspectionDropdown value={formData[item.i2Key as keyof SetupFormData] as string} onChange={(v) => updateField(item.i2Key as keyof SetupFormData, v)} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>

          {/* Sign Off - Full Width */}
          <div className="xl:col-span-2">
            <Section title="Sign Off" subtitle="Completion status and sign off">
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex items-center justify-between p-2 rounded bg-muted/20">
                    <span className="text-xs">Profiles</span>
                    <StatusBadge complete={isVehicleProfilesComplete} />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded bg-muted/20">
                    <span className="text-xs">Signage</span>
                    <StatusBadge complete={isSignageComplete} />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded bg-muted/20">
                    <span className="text-xs">Hardware</span>
                    <StatusBadge complete={isHardwareComplete} />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded bg-muted/20">
                    <span className="text-xs">Inspection</span>
                    <StatusBadge complete={isInspectionComplete} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Completed By</Label>
                    <Input value={formData.completed_by} onChange={(e) => updateField("completed_by", e.target.value)} placeholder="Admin name..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.completed_date && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                          {formData.completed_date ? formatDate(formData.completed_date) : "Select date..."}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={formData.completed_date || undefined} onSelect={(d) => updateField("completed_date", d || null)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea placeholder="Add any notes about the vehicle setup..." value={formData.notes} onChange={(e) => updateField("notes", e.target.value)} rows={1} />
                  </div>
                </div>
              </div>
            </Section>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default VehicleSetupEditModal;
